export enum AchievementType {
  ROBOT_MASTER = 'robot_master',
  DRONE_COORDINATOR = 'drone_coordinator',
  RESOURCE_SHARER = 'resource_sharer',
  ABILITY_SHARER = 'ability_sharer',
  RESCUER = 'rescuer',
  TEAM_PLAYER = 'team_player',
  URIDIUM_COLLECTOR = 'uridium_collector',
  EFFICIENCY_EXPERT = 'efficiency_expert',
  SURVIVOR = 'survivor',
  ACTIVE_EXPLORER = 'active_explorer',
  PUZZLE_SOLVER = 'puzzle_solver',
  ABILITY_USER = 'ability_user'
}

export enum AchievementTier {
  BRONZE = 'bronze',
  SILVER = 'silver',
  GOLD = 'gold'
}

// Best-per-loop values for each metric - STORED in DB, not re-fetched.
// Updated each time a slot is processed by taking max (higher-is-better) or min (lower-is-better).
export type AchievementMetricTotals = {
  timesUnlockedRobot: number
  timesSuccessfullyGotDrone: number
  timesSharedResourcesToOthers: number
  timesSharedAbilityToOthers: number
  teamPlayerTotal: number
  rescuedPlayersCount: number
  uridiumCollectedTotal: number
  timeParalyzed: number
  inactiveTimeTotal: number
  timeSpentOnDronePuzzle: number
  timesUsedAbility: number
  totalNumberOfTanksFilledWhenReturning: number
}

// Progress for a single achievement
// Keeps history of ALL tier unlocks (bronze, silver, gold)
export type AchievementProgress = {
  currentValue: number
  currentTier: AchievementTier | null
  history: Array<{
    tier: AchievementTier
    unlockedAt: string
    sessionId: string
    slotId: string
    teamId: string
    sprintId?: string
  }>
}

// User's complete achievements document
// Path: achievements/{userId}
export type UserAchievements = {
  userId: string
  lastUpdated: string

  // Running totals - updated incrementally, never re-fetched
  metricTotals: AchievementMetricTotals

  // Summary counts
  bronzeCount: number
  silverCount: number
  goldCount: number

  // Unseen achievements (for delayed display on FE)
  unseenAchievements: Array<{
    achievementId: AchievementType
    tier: AchievementTier
    unlockedAt: string
    sessionId: string
    slotId: string
    teamId: string
    sprintId?: string
  }>

  // All achievements with their current state
  // Each starts as null, gets populated when first earned
  achievements: Record<AchievementType, AchievementProgress | null>
}

export type AchievementThresholds = { bronze: number; silver: number; gold: number }

/** Where a person stands on one achievement, with the tiers in use and their direction. */
export type AchievementStanding = {
  value: number
  thresholds: AchievementThresholds
  /** The lower the value, the better (gold is the lowest number). */
  isLowerBetter: boolean
  currentTier: AchievementTier | null
  nextTier: AchievementTier | null
  /** 0 to 100 towards the next tier; 100 at gold. */
  progressPercent: number
}

/** The stored achievements with every standing, as the app receives them. */
export type UserAchievementsView = UserAchievements & { standings: Record<AchievementType, AchievementStanding> }

// Definition for a single achievement type
// Used in achievementDefinitions.ts
export type AchievementDefinition = {
  id: AchievementType
  name: string
  description: string
  metric: string | string[] // single field or array for combined metrics
  thresholds: AchievementThresholds
  lowerIsBetter?: boolean // for SURVIVOR, ACTIVE_EXPLORER
}

// Result when an achievement tier is unlocked
export type AchievementUnlockResult = {
  userId: string
  achievementId: AchievementType
  tier: AchievementTier
  slotId: string
  teamId: string
  sprintId?: string
}

// Session-level tracking of achievements unlocked
// Stored on session document as achievementsUnlocked field
export type SessionAchievementsUnlocked = Record<
  string, // userId
  Array<{
    achievementId: AchievementType
    tier: AchievementTier
    slotId: string
  }>
>

export function createEmptyMetricTotals(): AchievementMetricTotals {
  return {
    timesUnlockedRobot: 0,
    timesSuccessfullyGotDrone: 0,
    timesSharedResourcesToOthers: 0,
    timesSharedAbilityToOthers: 0,
    teamPlayerTotal: 0,
    rescuedPlayersCount: 0,
    uridiumCollectedTotal: 0,
    // Lower-is-better metrics use -1 as "unset" sentinel to avoid accidental unlocks.
    timeParalyzed: -1,
    inactiveTimeTotal: -1,
    timeSpentOnDronePuzzle: 0,
    timesUsedAbility: 0,
    totalNumberOfTanksFilledWhenReturning: -1
  }
}

export function createEmptyUserAchievements(userId: string): UserAchievements {
  const achievements: Record<AchievementType, AchievementProgress | null> = {
    [AchievementType.ROBOT_MASTER]: null,
    [AchievementType.DRONE_COORDINATOR]: null,
    [AchievementType.RESOURCE_SHARER]: null,
    [AchievementType.ABILITY_SHARER]: null,
    [AchievementType.RESCUER]: null,
    [AchievementType.TEAM_PLAYER]: null,
    [AchievementType.URIDIUM_COLLECTOR]: null,
    [AchievementType.EFFICIENCY_EXPERT]: null,
    [AchievementType.SURVIVOR]: null,
    [AchievementType.ACTIVE_EXPLORER]: null,
    [AchievementType.PUZZLE_SOLVER]: null,
    [AchievementType.ABILITY_USER]: null
  }

  return {
    userId: userId,
    lastUpdated: new Date().toISOString(),
    metricTotals: createEmptyMetricTotals(),
    bronzeCount: 0,
    silverCount: 0,
    goldCount: 0,
    unseenAchievements: [],
    achievements
  }
}

// ============================================================================
// Admin / Dev endpoints
// ============================================================================

/**
 * Admin-only endpoint to repopulate `unseenAchievements` from already earned achievements.
 * Used for testing the Unseen Achievements UX.
 */
export type UnseeAllAchievementsRequest = {
  /**
   * Target userId whose achievements doc will be modified.
   * If omitted, backend defaults to the authenticated caller.
   */
  userId?: string
}

export type UnseeAllAchievementsResponse = {
  success: boolean
  userId: string
  unseenCount: number
}
